import { gsap, Linear } from "gsap";
import * as PIXI from "pixi.js";
import { Dictionary } from "../../../util/collection/Dictionary";
import { EventObject } from "../../../util/event/EventObject";
import { Logger } from "../../../util/log/Logger";
import { TitanSpine } from "../../control/TitanSpine";
import { TitanSprite } from "../../control/TitanSprite";
import { ResizeEvent } from "../../renderer/Renderer";
import { AbstractView } from "../AbstractView";
/**
 * OverlayParticle.ts
 */
export class OverlayParticle extends AbstractView {
    constructor(properties, portraitProperties, isClone = false) {
        super(properties, portraitProperties);
        this.id = properties.id;
        this.ease = properties.ease;
        this.source = new PIXI.Point();
        this.destination = new PIXI.Point();
        if (properties.spriteProperties) {
            this.entity = TitanSprite.createSprite(this, "entity", properties.spriteProperties, portraitProperties?.spriteProperties);
        }
        else if (properties.spineProperties) {
            this.entity = TitanSpine.createSpine(this, properties.spineProperties, portraitProperties?.spineProperties);
        }
        if (properties.source) {
            this.source = properties.source;
        }
        if (properties.destination) {
            this.destination = properties.destination;
        }
        this.duration = properties.duration ?? 1;
        this.delay = properties.delay ?? 0;
        this.responsive = true;
        this._isClone = isClone;
    }
    get isClone() {
        return this._isClone;
    }
    clone() {
        const clone = new OverlayParticle(this.landscapeProperties, this.portraitProperties, true);
        clone.source = this.source.clone();
        clone.destination = this.destination.clone();
        return clone;
    }
    getProperties() {
        return super.getProperties();
    }
}
/**
 * AbstractOverlay.ts
 */
export class AbstractOverlay extends AbstractView {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
        this.responsive = true;
    }
    create() {
        super.create();
        this.createParticles();
    }
    postCreate() {
        super.postCreate();
        Logger.addObjectRef(this, "Overlay");
    }
    createParticles() {
        this.particles = new Dictionary();
        this.uiProperties.particleProperties.map((props, index) => {
            return new OverlayParticle(this.landscapeProperties.particleProperties[index], this.portraitProperties?.particleProperties[index]);
        }).forEach(particle => this.particles.setValue(particle.id, particle));
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(ResizeEvent.TRACK_TARGETS, this.handleTrackTargets);
    }
    handleTrackTargets() {
        this.uiProperties.particleProperties.forEach(prop => {
            const particle = this.particles.getValue(prop.id);
            this.updateParticleTracking(particle);
        });
    }
    updateParticleTracking(particle) {
        const properties = particle.getProperties();
        if (properties.sourceId && particle) {
            this.updateTrackPos(particle.source, properties.sourceId, properties.sourceOffset);
        }
        if (properties.destinationId && particle) {
            this.updateTrackPos(particle.destination, properties.destinationId, properties.destinationOffset);
        }
    }
    updateTrackPos(pos, id, offset) {
        const trackingData = this.locationManager.getLocation(id);
        if (trackingData !== null) {
            const targetPos = trackingData.position;
            const targetOffset = offset || new PIXI.Point();
            let returnPos = new PIXI.Point();
            this.toLocal(targetPos, null, returnPos, false);
            pos.x = (returnPos.x + targetOffset.x) * this.scale.x;
            pos.y = (returnPos.y + targetOffset.y) * this.scale.y;
        }
    }
    addParticle(particleId, asClone = false) {
        const particle = this.particles.getValue(particleId);
        const cloneOrParticle = asClone ? particle.clone() : particle;
        this.addChild(cloneOrParticle);
        return cloneOrParticle;
    }
    tweenParticle(particleId) {
        const particle = this.addParticle(particleId);
        particle.visible = true;
        particle.position.set(particle.source.x, particle.source.y);
        if (particle.entity instanceof TitanSpine) {
            particle.entity.play("animation", true);
        }
        gsap.to(particle.position, {
            x: particle.destination.x,
            y: particle.destination.y,
            ease: (particle.ease || "Linear.easeNone"),
            duration: particle.duration,
            onStart: () => {
                this.dispatchCore(new EventObject(OverlayEvent.START.getType(), { particle: particle }));
            },
            onComplete: () => {
                this.removeChild(particle.entity);
                this.dispatchCore(new EventObject(OverlayEvent.END.getType(), { particle: particle }));
                AbstractOverlay.destroyClone(particle);
            }
        });
    }
    manualParticle(particleId, onSpawn, onComplete, asClone = false) {
        const particle = this.addParticle(particleId, asClone);
        this.updateParticleTracking(particle);
        particle.visible = true;
        particle.position.set(particle.source.x, particle.source.y);
        if (typeof (onSpawn) === "function") {
            onSpawn(particle);
        }
        ;
        gsap.to(particle.position, {
            x: particle.destination.x,
            y: particle.destination.y,
            ease: (particle.ease || Linear.easeNone),
            duration: particle.duration,
            delay: particle.delay || 0,
            onComplete: () => {
                if (typeof (onComplete) === "function") {
                    onComplete(particle);
                }
                AbstractOverlay.destroyClone(particle);
            }
        });
    }
    static destroyClone(particle) {
        if (particle.isClone) {
            particle.destroy();
        }
    }
}
/**
 * OverlayEvent.ts
 */
export class OverlayEvent {
}
OverlayEvent.START = new EventObject("OverlayEvent.START");
OverlayEvent.END = new EventObject("OverlayEvent.END");
//# sourceMappingURL=AbstractOverlay.js.map